import { Copy } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";

import type { LicenceStatus } from "../api/types";
import { Callout } from "../components/Callout";
import { Dialog, DialogBody } from "../components/Dialog";
import { Motion, Presence } from "../components/presence";
import { Button } from "../components/ui";
import { t } from "../i18n/i18n";

/** Déplacer la licence vers un autre poste (demande d'Alexis du 30/10/2026) : fenêtre guidée en trois temps dans la
 *  fiche de la licence — ce qui suit la licence, le code à reporter sur l'autre poste, la libération de ce poste —,
 *  puis un écran final qui redonne le code. Aucune logique propre : la libération est celle de « Retirer la licence
 *  de ce poste » (`POST /licence/deactivate`) ; l'autre poste s'active comme tout poste, avec le même code. Les
 *  dossiers, ordres de bataille construits et ENI personnalisés sont rangés par licence (`spaces.space_of` du
 *  serveur), pas par poste : ils se retrouvent sur l'autre poste. */

export type MoveStep = "explain" | "code" | "confirm";

export const MOVE_STEPS: readonly MoveStep[] = ["explain", "code", "confirm"];

/** Temps suivant (« Continuer ») ; aucun après la confirmation, qui libère le poste. */
export function nextStep(step: MoveStep): MoveStep | null {
  return MOVE_STEPS[MOVE_STEPS.indexOf(step) + 1] ?? null;
}

/** Temps précédent (« Retour ») ; avant le premier, la fiche de la licence (« Annuler »). */
export function previousStep(step: MoveStep): MoveStep | null {
  return MOVE_STEPS[MOVE_STEPS.indexOf(step) - 1] ?? null;
}

/** Code masqué par le serveur (`GEN-REG-2026-••••-••••`) : comptes nominatifs sans administrateur connecté, ou code
 *  d'amorçage émis (docs/17 §5.7). Il ne se copie pas ; la fiche dit où le retrouver. */
export function codeMasked(code: string): boolean {
  return code.includes("•");
}

/** Code de licence en clair (ou masqué, selon la règle du serveur) et « Copier » quand il est en clair. */
export function LicenceCodeBox({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const masked = codeMasked(code);
  const copy = () => {
    void navigator.clipboard?.writeText(code).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };
  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 rounded-lg border border-line bg-canvas px-3 py-2 text-center font-mono text-ui tracking-wider break-all text-ink select-all">
          {code}
        </div>
        {!masked && (
          <Button variant="secondary" size="sm" onClick={copy}>
            <Copy size={14} /> {t("Copier")}
          </Button>
        )}
      </div>
      <Presence show={copied}>
        {copied && (
          <Callout tone="ok" className="mt-2">
            {t("Code copié : collez-le à l'écran d'activation de l'autre poste.")}
          </Callout>
        )}
      </Presence>
      <Presence show={masked}>
        {masked && (
          <Callout tone="info" className="mt-2">
            {t("Le code complet n'est montré qu'aux administrateurs de l'unité. Reprenez-le sur le document de licence remis par BELLICOM, ou demandez-le à un administrateur de l'unité.")}
          </Callout>
        )}
      </Presence>
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-1 list-disc space-y-1 pl-5 text-ui leading-snug text-ink-soft">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

/** Temps 1 : ce qui se passe, ce qui suit la licence et ce qui reste sur ce poste. */
export function MoveExplain({ status, readOnly }: { status: LicenceStatus; readOnly: boolean }) {
  const accounts = status.accounts?.mode === "accounts";
  const follows = [
    t("Les dossiers d'exercice, les ordres de bataille construits et les ENI personnalisés : ils appartiennent à la licence, pas au poste, et se retrouvent tels quels sur le nouveau poste."),
    t("L'espace de stockage de la licence et sa date d'échéance."),
  ];
  if (accounts) follows.push(t("Les comptes nominatifs : chacun se connecte sur le nouveau poste avec son identifiant et son mot de passe, et y retrouve ses dossiers et son rôle dans la DIREX."));
  const stays = [
    t("Les saisies non enregistrées et l'état de l'affichage gardés par ce navigateur : ils en sont effacés."),
    t("La langue, le thème et les préférences de la carte : ce sont des réglages du navigateur, à refaire sur le nouveau poste."),
  ];
  // Mode comptes : le rôle DIREX et la notice sont ceux de la personne, qui les retrouve sur le nouveau poste.
  if (!accounts) {
    stays.push(t("Le rôle de ce poste dans la DIREX (animateur, cellule, observateur) : le nouveau poste choisit le sien, animateur tant qu'il n'a rien choisi."));
    stays.push(t("L'acceptation de la notice de protection des données : elle est à donner de nouveau sur le nouveau poste."));
  }
  return (
    <Motion origin="fade" className="space-y-3">
      <p className="text-ui leading-relaxed text-ink-soft">
        {t("La licence quitte ce poste et s'active sur l'autre avec le même code. Aucun dossier n'est supprimé.")}
      </p>
      <div>
        <h3 className="text-ui font-semibold text-ink">{t("Ce qui suit la licence")}</h3>
        <Bullets items={follows} />
      </div>
      <div>
        <h3 className="text-ui font-semibold text-ink">{t("Ce qui ne suit pas")}</h3>
        <Bullets items={stays} />
      </div>
      {status.trial && (
        <Callout tone="info">
          {t("Licence d'essai : le déplacement est permis. Le nouveau poste reste en licence d'essai, aux mêmes conditions et jusqu'à la même date.")}
        </Callout>
      )}
      {status.source === "offline" && (
        <Callout tone="info" title={t("Poste activé par un code hors ligne")}>
          {t("Un code hors ligne n'ouvre qu'un seul poste : il ne servira pas sur l'autre. Activez celui-ci avec le code de licence de l'étape suivante, vérifié en ligne, ou demandez à BELLICOM un nouveau code hors ligne pour lui. Les dossiers suivent dans les deux cas.")}
        </Callout>
      )}
      {readOnly && (
        <Callout tone="warn" title={t("Licence expirée")}>
          {t("Aucun poste ne peut être activé avant le renouvellement de la licence : l'autre poste ne s'ouvrira qu'une fois la licence renouvelée, et ce poste perdra d'ici là la lecture seule. Renouvelez d'abord la licence auprès de BELLICOM.")}
        </Callout>
      )}
    </Motion>
  );
}

/** Temps 2 : le code à reporter et les étapes à suivre sur l'autre poste. */
export function MoveCode({ status }: { status: LicenceStatus }) {
  const code = status.licence_code ?? "";
  const accounts = status.accounts?.mode === "accounts";
  const known = status.source === "online" && status.activated_devices != null && status.max_devices != null;
  const full = known && (status.activated_devices ?? 0) >= (status.max_devices ?? 0);
  return (
    <Motion origin="fade" className="space-y-3">
      <div>
        <h3 className="text-ui font-semibold text-ink">{t("Code de licence")}</h3>
        <div className="mt-1.5">
          <LicenceCodeBox code={code} />
        </div>
      </div>
      <div>
        <h3 className="text-ui font-semibold text-ink">{t("Sur l'autre poste")}</h3>
        <ol className="mt-1 list-decimal space-y-1 pl-5 text-ui leading-snug text-ink-soft">
          <li>{t("Ouvrez Generex Web dans le navigateur, à l'adresse {adresse}.", { adresse: window.location.host })}</li>
          <li>{t("À l'écran d'activation, saisissez ce code et donnez un nom au poste.")}</li>
          <li>
            {accounts
              ? t("Connectez-vous avec votre identifiant et votre mot de passe : vos dossiers s'affichent.")
              : t("Acceptez la notice de protection des données : les dossiers de la licence s'affichent.")}
          </li>
        </ol>
      </div>
      {known && (
        <Callout tone={full ? "warn" : "info"}>
          {full
            ? t("Toutes les places de la licence sont prises ({active} sur {max}) : activez l'autre poste après avoir libéré celui-ci, à l'étape suivante.", {
                active: status.activated_devices ?? 0,
                max: status.max_devices ?? 0,
              })
            : t("La licence a encore une place libre : l'autre poste peut s'activer dès maintenant, avant ou après la libération de celui-ci.")}
        </Callout>
      )}
      <p className="text-meta leading-snug text-ink-muted">
        {t("Sur un iPad, le même code s'active dans l'application Generex pour iPad ; les dossiers de Generex Web n'y apparaissent pas.")}
      </p>
    </Motion>
  );
}

/** Temps 3 : ce que fait la confirmation. Les avertissements (licence expirée, saisies non enregistrées, refus)
 *  sont ceux de la fiche, affichés sous ce texte. */
export function MoveConfirm() {
  return (
    <Motion origin="fade">
      <p className="text-ui leading-relaxed text-ink-soft">
        {t("Ce poste rend sa place à la licence et perd aussitôt l'accès à ses dossiers. Les dossiers restent à la licence : l'autre poste les retrouve dès son activation.")}
      </p>
    </Motion>
  );
}

/** Contenu de l'écran final : le code à reporter, redonné. */
export function MovedBody({ code }: { code: string }) {
  return (
    <DialogBody>
      <Callout tone="ok">{t("Licence libérée : activez-la sur l'autre poste avec ce code.")}</Callout>
      <LicenceCodeBox code={code} />
      <p className="text-ui leading-relaxed text-ink-soft">
        {t("Ce poste n'a plus accès aux dossiers de la licence. « Terminé » ramène à l'écran d'activation.")}
      </p>
    </DialogBody>
  );
}

/** Écran final, après la libération. Rendu au niveau du document, au-dessus de l'écran d'activation que le portail de
 *  licence peut afficher dès que ce poste n'est plus reconnu (plan z-95) ; la fenêtre entre en douceur, comme toute
 *  fenêtre, et n'a pas de sortie à jouer : « Terminé » (comme Échap ou la croix) recharge la page sur l'écran
 *  d'activation. */
export function MovedDialog({ code, onDone }: { code: string | null; onDone: () => void }) {
  if (code === null) return null;
  return createPortal(
    <div className="relative z-[96]">
      <Dialog title={t("Licence libérée")} width={420} onClose={onDone} footer={<Button onClick={onDone}>{t("Terminé")}</Button>}>
        <MovedBody code={code} />
      </Dialog>
    </div>,
    document.body,
  );
}
